import type { ReactNode } from "react";
import { useTilt } from "../../hooks/useTilt";

/**
 * Wraps content in a pointer-tilted, holo-sheened surface. On touch devices the
 * tilt is skipped and it degrades to a plain rounded surface.
 */
export function HoloCard({
  children,
  className = "",
  holo = 0.3,
  max = 10,
  lift = 14,
  scale = 1.02,
  fill = false,
}: {
  children: ReactNode;
  className?: string;
  /** Sheen strength 0-1. */
  holo?: number;
  max?: number;
  lift?: number;
  scale?: number;
  /** Stretch both wrappers to the parent box instead of hugging the content. */
  fill?: boolean;
}) {
  const { ref, active, tiltProps, enabled } = useTilt({ max, lift, scale });
  const box = fill ? "h-full w-full" : "";
  // Keep the sheen faint at rest so ordinary product photos aren't washed out;
  // it blooms only while the pointer is on the card.
  const strength = active ? holo : holo * 0.3;

  // With no pointer to react to (touch, or reduced motion), --mx/--my never
  // move, so the sheen pseudo-elements would sit frozen — a static, hard-edged
  // rainbow-bar pattern glued to the card. Skip the class entirely there and
  // fall back to a genuinely plain surface, as the component promises.
  return (
    <div className={`${enabled ? "stage-3d" : ""} ${box}`}>
      <div
        ref={ref}
        {...tiltProps}
        style={enabled ? { ["--holo" as string]: strength } : undefined}
        className={`${enabled ? "holo-surface" : ""} will-change-transform ${box} ${className}`}
      >
        {children}
      </div>
    </div>
  );
}
